/**
 * Il guscio comune delle viste di un allenamento (V13, V66, V14, V67, V15),
 * rotta padre delle schede: carica l'allenamento una volta, tiene la testata
 * con data, luogo, stato e i comandi (modifica, cambia stato, elimina) e sotto
 * le schede nel componente Tabs. Cambiando scheda cambia solo il corpo,
 * come nella scheda giocatore; le schede si sostituiscono nella cronologia,
 * e «indietro» riporta a chi ha aperto l'allenamento.
 *
 * Le schede leggono dal contesto dell'`Outlet` l'allenamento caricato; dopo
 * un comando della testata si rimontano e ricaricano i loro dati. Tornando
 * al Riepilogo l'allenamento si ricarica, perché appello e voti lo cambiano.
 *
 * I pezzi che le schede si passano (riga dei comandi, stato del salvataggio,
 * card) stanno in `comuni/Riquadri.tsx`, i pulsanti in `comuni/Azioni.tsx`.
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { dataLocale, puo } from '@calcio/shared';
import { BanIcon, PencilIcon, RotateCcwIcon, Trash2Icon, Undo2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { chiama } from '../api/client.js';
import { type Domanda, useConferma } from '../comuni/Conferma.js';
import { Corpo } from '../comuni/Corpo.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { AzioniTestata, MenuAzioni, PulsantePrincipale } from '../comuni/Azioni.js';
import { Testata } from '../comuni/Testata.js';
import { useContesto } from '../navigazione/contesto.js';
import { VistaStato } from '../navigazione/NonTrovata.js';
import { type ContestoAllenamento } from './contesto.js';
import { DialogAllenamento } from './FormAllenamento.js';
import {
  COLORE_SEGNO,
  type ComposizioneSeduta,
  type DettaglioAllenamento,
  ETICHETTA_SEGNO,
  type StatoAllenamento,
  conteggioPiano,
  descrizioneEliminazione,
  formatoDataAllenamento,
  formatoOra,
  segnoDi,
} from './allenamenti.js';

type Scheda = 'riepilogo' | 'componi' | 'registro' | 'appello' | 'voti';

/** Scheda, coda del percorso ed etichetta, nell'ordine dei Tabs. */
const SCHEDE: readonly (readonly [Scheda, string, string])[] = [
  ['riepilogo', '', 'Riepilogo'],
  ['componi', '/blocchi', 'Componi'],
  ['appello', '/presenze', 'Appello'],
  ['registro', '/registro', 'Registro seduta'],
  ['voti', '/voti', 'Voti'],
];

export function PaginaAllenamento() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { utente } = useContesto();
  const { dati, errore, ricarica } = useDati<DettaglioAllenamento>(`/allenamenti/${id}`);
  const [stato, setStato] = useState<StatoAllenamento | null>(null);
  const [versione, setVersione] = useState(0);
  const [modifica, setModifica] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [chiedi, conferma] = useConferma();

  const base = `/sq/${ss}/allenamenti/${id}`;
  const coda = pathname.slice(base.length).replace(/\/$/, '');
  const scheda = SCHEDE.find(([, c]) => c === coda)?.[0] ?? 'riepilogo';

  const aggiorna = async () => {
    await ricarica();
    setStato(null);
  };

  // Presenze e voti del Riepilogo cambiano nelle altre schede: tornandoci si rileggono.
  const precedente = useRef(scheda);
  useEffect(() => {
    if (scheda === 'riepilogo' && precedente.current !== 'riepilogo') void aggiorna();
    precedente.current = scheda;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheda]);

  if (!ss || !id) return null;
  if (errore) {
    return (
      <VistaStato
        titolo="Allenamento non disponibile"
        uscita={{ a: `/sq/${ss}/allenamenti`, etichetta: 'Torna agli allenamenti' }}
      >
        {errore}
      </VistaStato>
    );
  }
  const a = dati?.allenamento.id === id ? dati.allenamento : undefined;
  if (a && a.squadraStagioneId !== ss) {
    return <Navigate to={pathname.replace(`/sq/${ss}/`, `/sq/${a.squadraStagioneId}/`)} replace />;
  }

  const visibili = SCHEDE.filter(
    ([s]) =>
      (s !== 'appello' || puo(utente.assegnazioni, 'presenza.scrivi', ss)) &&
      (s !== 'voti' || puo(utente.assegnazioni, 'voto.leggi', ss)),
  );
  const statoAttuale = stato ?? a?.stato;
  const segno =
    a && statoAttuale ? segnoDi({ ...a, stato: statoAttuale }, dataLocale(new Date())) : null;
  const gestisce = puo(utente.assegnazioni, 'allenamento.gestisci', ss);

  /** Dopo un comando la testata si rilegge e la scheda aperta si rimonta. */
  const dopoComando = async () => {
    await aggiorna();
    setVersione((v) => v + 1);
  };

  const cambiaStato = async (nuovo: StatoAllenamento, domanda?: Domanda) => {
    if (domanda && !(await chiedi(domanda))) return;
    setInCorso(true);
    try {
      await chiama(`/allenamenti/${id}/stato`, { metodo: 'PUT', corpo: { stato: nuovo } });
      await dopoComando();
      toast.success(
        nuovo === 'annullato'
          ? 'Allenamento annullato.'
          : nuovo === 'programmato' && statoAttuale === 'svolto'
            ? 'Allenamento tornato programmato.'
            : 'Allenamento ripristinato.',
      );
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(false);
    }
  };

  const elimina = async () => {
    if (!dati) return;
    setInCorso(true);
    // Gruppi e blocchi si contano al momento: la composizione cambia nell'altra scheda.
    const piano = await chiama<ComposizioneSeduta>(`/allenamenti/${id}/composizione`).catch(
      () => null,
    );
    setInCorso(false);
    const si = await chiedi({
      titolo: "Eliminare l'allenamento?",
      testo: `${descrizioneEliminazione({
        presenze: dati.presenze,
        voti: dati.voti,
        ...(piano ? conteggioPiano(piano) : {}),
        log: dati.log,
      })} L'operazione non si annulla: se l'allenamento non si è fatto, puoi annullarlo invece di eliminarlo.`,
      azione: 'Elimina',
      distruttiva: true,
    });
    if (!si) return;
    setInCorso(true);
    try {
      await chiama(`/allenamenti/${id}`, { metodo: 'DELETE' });
      toast.success('Allenamento eliminato.');
      navigate(`/sq/${ss}/allenamenti`, { replace: true });
    } catch (e) {
      toast.error(messaggio(e));
      setInCorso(false);
    }
  };

  const arrivato = a ? dataLocale(a.dataOra) <= dataLocale(new Date()) : false;
  const azioni = gestisce && dati && a && (
    <AzioniTestata>
      <PulsantePrincipale
        etichetta="Modifica"
        Icona={PencilIcon}
        onClick={() => setModifica(true)}
      />
      <MenuAzioni
        voci={[
          statoAttuale === 'svolto' && {
            etichetta: 'Torna programmato',
            Icona: Undo2Icon,
            disabled: inCorso,
            onSelect: () => void cambiaStato('programmato'),
          },
          statoAttuale === 'annullato'
            ? {
                etichetta: 'Ripristina',
                Icona: RotateCcwIcon,
                disabled: inCorso,
                // Con l'appello gia fatto l'allenamento c'e stato: torna svolto.
                onSelect: () =>
                  void cambiaStato(dati.presenze > 0 && arrivato ? 'svolto' : 'programmato'),
              }
            : {
                etichetta: "Annulla l'allenamento",
                Icona: BanIcon,
                disabled: inCorso,
                onSelect: () =>
                  void cambiaStato('annullato', {
                    titolo: "Annullare l'allenamento?",
                    testo: 'Le presenze restano, ma non contano nelle statistiche.',
                    azione: "Annulla l'allenamento",
                  }),
              },
          {
            etichetta: 'Elimina',
            Icona: Trash2Icon,
            disabled: inCorso,
            onSelect: () => void elimina(),
            distruttiva: true,
          },
        ]}
      />
    </AzioniTestata>
  );

  const contesto: ContestoAllenamento | undefined =
    dati && a ? { dettaglio: dati, ricarica: aggiorna, aggiornaStato: setStato } : undefined;

  return (
    <div className="pb-6">
      <Testata
        indietro={`/sq/${ss}`}
        sopra={
          a
            ? `${formatoDataAllenamento.format(new Date(a.dataOra))} ${formatoOra.format(new Date(a.dataOra))} · ${a.luogo ?? 'luogo da definire'}`
            : ' '
        }
        titolo="Allenamento"
        accanto={segno && <Badge className={COLORE_SEGNO[segno]}>{ETICHETTA_SEGNO[segno]}</Badge>}
        azione={azioni}
      >
        {scheda === 'riepilogo' && a?.note && (
          <p className="text-sm whitespace-pre-line text-muted-foreground">{a.note}</p>
        )}
      </Testata>

      <Corpo className="flex flex-col gap-4">
        <Tabs value={scheda}>
          <TabsList className="w-full justify-start overflow-x-auto [scrollbar-width:none]">
            {visibili.map(([valore, c, etichetta]) => (
              <TabsTrigger key={valore} value={valore} asChild>
                <NavLink to={`${base}${c}`} end replace>
                  {etichetta}
                </NavLink>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Outlet key={versione} context={contesto} />
      </Corpo>

      {dati && gestisce && (
        <DialogAllenamento
          ss={ss}
          aperto={modifica}
          esistente={dati}
          onChiudi={() => setModifica(false)}
          onSalvato={dopoComando}
        />
      )}
      {conferma}
    </div>
  );
}
